/** Diamonds — what the learner has to show for the work they have done.
 *
 * The client, after a child could not stay interested in the day-one
 * screens: "we can gamify it. like getting the diamonds on successfully
 * finishing the level, or teil etc".
 *
 * The learners are eight to twenty-six, which rules out most of what
 * gamification usually means. No mascot congratulating you, no confetti,
 * no streak nagging you about yesterday: a twenty-six-year-old closes the
 * tab. What both ages want from a reward is the same small thing — a
 * number that goes up for real work, and a clear answer to "what do I do
 * to make it go up again".
 *
 * So this is a count and a price list, nothing more. The price list is
 * the part that changes behaviour: a total on its own says you have 63 of
 * something; the breakdown says which thing to do next to make it 64.
 *
 * The figures are DERIVED server-side from achievements already recorded
 * (see backend rewards.py) — there is no balance anywhere, so this can
 * never drift from what the learner actually did, and refetching is
 * always safe.
 */
import { useEffect, useState } from "react";
import { myRewards, type RewardBucket, type Rewards } from "../../api/learning";
import { Card } from "../../components/ui";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

function Row(
  { bucket, labelKey }: { bucket: RewardBucket; labelKey: MessageKey },
) {
  const t = useT();
  return (
    <li className="flex items-center justify-between gap-3 border-b border-line/60 py-2 last:border-0">
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{t(labelKey)}</span>
        {/* The price, always shown. A reward a learner cannot predict does
            not change what they do next. */}
        <span className="block text-xs text-muted">
          {t("rewards.each", { n: bucket.each })}
        </span>
      </span>
      <span className="flex shrink-0 items-baseline gap-1 tabular-nums">
        <span className="text-sm text-muted">{bucket.done} ×</span>
        <span className="text-base font-extrabold text-brand-dark">
          {bucket.diamonds}
        </span>
        <span aria-hidden>💎</span>
      </span>
    </li>
  );
}

export function DiamondBoard() {
  const t = useT();
  const [rewards, setRewards] = useState<Rewards | null>(null);

  useEffect(() => {
    let cancelled = false;
    myRewards()
      .then((r) => {
        if (!cancelled) setRewards(r);
      })
      // A board we cannot load is not worth an error in front of a
      // learner — the rest of the dashboard is the useful part.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!rewards) return null;

  return (
    <Card title={t("rewards.title")} tone="accent">
      <div className="flex items-center gap-4">
        {/* Gold, because they are diamonds: the one tile on the dashboard
            that is a reward rather than a measure. */}
        <span
          aria-hidden
          className="bg-grad-accent grid h-16 w-16 shrink-0 place-items-center rounded-card text-3xl shadow-sm"
        >
          💎
        </span>
        <div className="min-w-0">
          <p className="text-3xl font-extrabold leading-none text-brand-dark tabular-nums">
            {rewards.total}
          </p>
          <p className="mt-1 text-sm text-muted">
            {rewards.total === 0 ? t("rewards.none") : t("rewards.subtitle")}
          </p>
        </div>
      </div>

      <ul className="mt-4">
        <Row bucket={rewards.lessons} labelKey="rewards.lessons" />
        <Row bucket={rewards.teile} labelKey="rewards.teile" />
        <Row bucket={rewards.levels} labelKey="rewards.levels" />
      </ul>
    </Card>
  );
}
